import { useEffect, useState } from "react";
import { g } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { SetupWizard } from "@/pages/Setup";
import { Questionario } from "@/components/Questionario";
import { NEWS, NEWS_VERSIONE } from "@/data/news";
import {
  UtensilsCrossed, Volume2, SplitSquareHorizontal, Bike, Boxes, BellRing,
  ArrowRight, Check, Sparkles, Globe, Timer, TrendingDown, Eye, ShieldCheck, EyeOff, X,
  QrCode, MailCheck,
} from "lucide-react";

const LangSwitch = () => {
  const { lang, setLang, langs } = useI18n();
  return (
    <>
      {/* mobile: menu compatto, così la barra non straborda */}
      <div className="relative sm:hidden" data-testid="lang-switch-mobile">
        <Globe className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-500" />
        <select
          value={lang}
          onChange={(e) => setLang(e.target.value)}
          data-testid="lang-select"
          aria-label="Lingua"
          className="appearance-none rounded-full border border-slate-700 bg-slate-900/70 py-1.5 pl-7 pr-2 text-[11px] font-bold text-slate-200"
        >
          {langs.map((l) => (
            <option key={l.id} value={l.id}>
              {l.flag}
            </option>
          ))}
        </select>
      </div>

      <div className="hidden items-center gap-1 rounded-full border border-slate-700 bg-slate-900/70 p-1 sm:flex" data-testid="lang-switch">
        <Globe className="ml-1.5 h-3.5 w-3.5 text-slate-500" />
        {langs.map((l) => (
          <button
            key={l.id}
            data-testid={`lang-${l.id}`}
            onClick={() => setLang(l.id)}
            className={`rounded-full px-2.5 py-1 text-[11px] font-bold transition-colors ${
              lang === l.id ? "bg-amber-500 text-slate-950" : "text-slate-400 hover:text-slate-100"
            }`}
          >
            {l.flag}
          </button>
        ))}
      </div>
    </>
  );
};

const FEATURES = [
  ["orders", UtensilsCrossed, "md:col-span-7"],
  ["voice", Volume2, "md:col-span-5"],
  ["split", SplitSquareHorizontal, "md:col-span-4"],
  ["calls", QrCode, "md:col-span-4"],
  ["report", MailCheck, "md:col-span-4"],
  ["delivery", Bike, "md:col-span-4"],
  ["stock", Boxes, "md:col-span-4"],
  ["alerts", BellRing, "md:col-span-4"],
];

export default function Landing({ onEnter, startQuiz = false }) {
  const { t, lang } = useI18n();
  const [quiz, setQuiz] = useState(startQuiz);
  const [plans, setPlans] = useState([]);
  const [cycle, setCycle] = useState("year");
  const [feedback, setFeedback] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  // Solo su un dispositivo che ha già usato la console: scorciatoia per rientrare
  const isVendor = Boolean(localStorage.getItem("rh_admin") || localStorage.getItem("rh_admin_device"));

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("parere")) setFeedback(true);  }, []);

  // Passato l'hero, il tasto d'accesso lascia il posto al «Prova gratis»: mai i due insieme.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 420);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    g("/plan")
      .then((d) => setPlans(d.plans || []))
      .catch(() => setPlans([]));
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50" data-testid="landing-page">
      <header className="sticky top-0 z-40 border-b border-slate-800 bg-slate-950/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-2 px-4 py-3 sm:gap-3 sm:px-6 sm:py-4 lg:px-8">
          <div className="flex min-w-0 shrink items-baseline gap-1.5">
            <span className="font-head truncate text-lg font-bold tracking-tight sm:text-xl">Reprehendo</span>
            <span className="hidden text-[10px] font-bold uppercase tracking-[0.24em] text-amber-500 sm:inline">RH</span>
          </div>
          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
            {isVendor && (
              <Button
                variant="ghost"
                className="h-9 shrink-0 px-2 text-xs text-slate-400 hover:text-amber-400"
                onClick={() => { window.location.href = "/rh-console"; }}
                data-testid="landing-console-btn"
              >
                Console
              </Button>
            )}
            <LangSwitch />
            <Button variant="ghost" className="hidden text-slate-300 hover:text-white sm:inline-flex" onClick={onEnter} data-testid="landing-enter-btn">
              {t("nav.enter")}
            </Button>
            {/* Il tasto arancione appare solo dopo l'hero: sopra farebbe il doppione del CTA grande. */}
            {scrolled ? (
              <Button className="h-9 shrink-0 bg-amber-500 px-3 text-xs text-slate-950 hover:bg-amber-400 sm:h-10 sm:px-4 sm:text-sm" onClick={() => setQuiz(true)} data-testid="landing-try-btn">
                {t("nav.try")}
              </Button>
            ) : (
              <Button variant="outline" className="h-9 shrink-0 border-slate-700 bg-transparent px-3 text-xs text-slate-200 hover:bg-slate-900 hover:text-white sm:hidden" onClick={onEnter} data-testid="landing-enter-mobile-btn">
                {t("nav.enter")}
              </Button>
            )}
          </div>
        </div>
      </header>

      <section className="mx-auto max-w-7xl px-6 pb-20 pt-20 lg:px-8 lg:pt-32">
        <div className="grid grid-cols-1 items-center gap-16 lg:grid-cols-2">
          <div className="fade-up max-w-2xl">
            <div className="flex flex-wrap items-center gap-2" data-testid="landing-beta-badge">
              <span className="rounded-full bg-amber-500 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-slate-950">Beta</span>
              <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">{t("beta.tag")}</span>
            </div>
            <p className="mt-4 text-xs font-semibold uppercase tracking-[0.2em] text-amber-500">{t("hero.badge")}</p>
            <h1 className="font-head mt-5 text-4xl font-bold leading-[1.08] tracking-tighter sm:text-5xl lg:text-6xl">
              {t("hero.title")}
            </h1>
            <p className="mt-6 text-base leading-relaxed text-slate-400 sm:text-lg">{t("hero.sub")}</p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <Button
                className="h-14 bg-amber-500 px-7 text-base text-slate-950 transition-transform hover:scale-[1.02] hover:bg-amber-400"
                onClick={() => setQuiz(true)}
                data-testid="hero-quiz-btn"
              >
                <Sparkles className="mr-2 h-4 w-4" /> {t("hero.cta")}
              </Button>
              <Button variant="ghost" className="h-14 px-7 text-base text-slate-300 hover:bg-slate-900 hover:text-white" onClick={onEnter} data-testid="hero-enter-btn">
                {t("hero.have")} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
            <p className="mt-4 text-xs text-slate-500">{t("hero.note")}</p>
            <p className="mt-3 max-w-xl rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-xs leading-relaxed text-amber-200" data-testid="landing-beta-note">
              {t("beta.notice")}{" "}
              <button onClick={() => setFeedback(true)} data-testid="landing-feedback-btn" className="font-bold underline decoration-amber-400/60 hover:text-amber-100">
                {t("beta.cta")}
              </button>
            </p>
          </div>

          <div className="fade-up relative hidden lg:block" aria-hidden="true">
            <div className="absolute -left-6 top-6 w-64 rotate-[-4deg] rounded-2xl border border-slate-800 bg-slate-900 p-5 shadow-2xl">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Tavolo S5</p>
              <div className="mt-3 space-y-2">
                {[["2× Margherita", "Pizzeria"], ["1× Carbonara", "Cucina"], ["3× Spritz", "Bar"]].map(([a, b]) => (
                  <div key={a} className="flex items-center justify-between rounded-lg bg-slate-800/60 px-3 py-2">
                    <span className="text-xs font-semibold">{a}</span>
                    <span className="text-[10px] text-amber-500">{b}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="ml-28 mt-24 w-64 rotate-[3deg] rounded-2xl border border-amber-500/30 bg-slate-900 p-5 shadow-[0_0_60px_-20px_rgba(245,158,11,0.45)]">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-500">Monitor Cucina</p>
              <p className="font-num mt-3 text-3xl font-bold">04′</p>
              <p className="mt-1 text-xs text-slate-400">2 piatti in preparazione</p>
              <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-slate-800">
                <div className="h-1.5 w-2/3 rounded-full bg-amber-500" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20 lg:px-8 lg:py-28">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-500">{t("feat.overline")}</p>
        <h2 className="font-head mt-3 max-w-2xl text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">{t("feat.title")}</h2>
        <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-12">
          {FEATURES.map(([key, Icon, span], i) => (
            <div
              key={key}
              data-testid={`feature-${key}`}
              style={{ animationDelay: `${i * 70}ms` }}
              className={`fade-up group rounded-3xl border border-slate-800 bg-slate-900 p-8 transition-colors hover:border-amber-500/40 ${span}`}
            >
              <span className="inline-flex rounded-xl bg-amber-500/10 p-3 transition-transform group-hover:scale-105">
                <Icon className="h-5 w-5 text-amber-500" />
              </span>
              <h3 className="font-head mt-5 text-lg font-bold">{t(`feat.${key}.t`)}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">{t(`feat.${key}.d`)}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-y border-slate-800 bg-slate-900/40">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-8 lg:py-28">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-500">{t("how.overline")}</p>
          <h2 className="font-head mt-3 text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">{t("how.title")}</h2>
          <div className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-3">
            {[1, 2, 3].map((n) => (
              <div key={n} className="fade-up" style={{ animationDelay: `${n * 90}ms` }} data-testid={`how-step-${n}`}>
                <span className="font-num flex h-11 w-11 items-center justify-center rounded-full border border-amber-500/40 text-lg font-bold text-amber-500">
                  {n}
                </span>
                <h3 className="font-head mt-5 text-lg font-bold">{t(`how.${n}.t`)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{t(`how.${n}.d`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-slate-800 bg-slate-900/40" data-testid="screens-section">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-8 lg:py-28">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-500">{t("shots.overline")}</p>
          <h2 className="font-head mt-3 max-w-2xl text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">{t("shots.title")}</h2>
          <p className="mt-3 max-w-xl text-sm text-slate-400">{t("shots.sub")}</p>

          <div className="mt-12 space-y-16">
            {["sala", "comanda", "monitor", "conto"].map((k, i) => (
              <div key={k} className={`fade-up grid grid-cols-1 items-center gap-8 lg:grid-cols-12 ${i % 2 ? "lg:[&>*:first-child]:order-2" : ""}`} data-testid={`shot-${k}`}>
                <div className="lg:col-span-7">
                  <div className="overflow-hidden rounded-2xl border border-slate-800 shadow-2xl">
                    <img
                      src={`/screens/${k}.png`}
                      alt={t(`shots.${k}.t`)}
                      loading="lazy"
                      className="block w-full"
                    />
                  </div>
                </div>
                <div className="lg:col-span-5">
                  <p className="font-num text-xs font-bold text-amber-500">0{i + 1}</p>
                  <h3 className="font-head mt-2 text-lg font-bold sm:text-lg">{t(`shots.${k}.t`)}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{t(`shots.${k}.d`)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-slate-800 bg-slate-900/40" data-testid="why-section">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-8 lg:py-28">
          <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
            <div className="fade-up lg:col-span-7">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-500">{t("why.overline")}</p>
              <h2 className="font-head mt-3 text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">{t("why.title")}</h2>
              <div className="mt-7 max-w-2xl border-l-2 border-amber-500/50 pl-6">
                <p className="text-base leading-relaxed text-slate-300" dangerouslySetInnerHTML={{ __html: t("why.p1") }} />
                <p className="font-head mt-5 text-lg italic leading-relaxed text-amber-500/90">{t("why.p2")}</p>
              </div>
              <p className="mt-7 max-w-2xl text-sm leading-relaxed text-slate-400">{t("why.p3")}</p>
            </div>

            <div className="fade-up space-y-4 lg:col-span-5" style={{ animationDelay: "120ms" }}>
              {[["why.q1", Timer], ["why.q2", TrendingDown], ["why.q3", Eye]].map(([k, Icon], i) => (
                <div key={k} className="flex items-start gap-4 rounded-2xl border border-slate-800 bg-slate-950/40 p-5" data-testid={`why-pillar-${i + 1}`}>
                  <span className="rounded-xl bg-amber-500/10 p-2.5">
                    <Icon className="h-4 w-4 text-amber-500" />
                  </span>
                  <div className="min-w-0">
                    <p className="font-head text-base font-bold">{t(k)}</p>
                    <p className="mt-1 text-sm leading-relaxed text-slate-400">{t(`${k}d`)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-slate-800 bg-slate-900/40" data-testid="news-section">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-8 lg:py-28">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-500">{t("news.overline")}</p>
              <h2 className="font-head mt-3 max-w-2xl text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
                {t("news.title")}
              </h2>
              <p className="mt-3 max-w-xl text-sm text-slate-400">{t("news.sub")}</p>
            </div>
            <span className="font-num rounded-full border border-amber-500/40 px-3 py-1 text-xs font-bold text-amber-500" data-testid="news-version">
              {t("news.more")} · {NEWS_VERSIONE}
            </span>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2">
            {NEWS.map((n, i) => {
              const v = n[lang] || n.it;
              return (
                <div
                  key={v.t}
                  data-testid={`news-item-${i}`}
                  style={{ animationDelay: `${i * 70}ms` }}
                  className="fade-up rounded-2xl border border-slate-800 bg-slate-950/50 p-7 transition-colors hover:border-amber-500/40"
                >
                  <p className="font-num text-xs font-bold text-amber-500">{n.data}</p>
                  <h3 className="font-head mt-2 text-lg font-bold">{v.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{v.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-y border-slate-800 bg-slate-950" data-testid="trust-section">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-8 lg:py-28">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-500">{t("trust.overline")}</p>
          <h2 className="font-head mt-3 max-w-3xl text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
            {t("trust.title")}
          </h2>
          <p className="mt-3 max-w-xl text-sm text-slate-400">{t("trust.sub")}</p>

          <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2">
            <div className="fade-up rounded-3xl border border-emerald-500/25 bg-emerald-500/[0.04] p-7" data-testid="trust-see">
              <p className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-400">
                <ShieldCheck className="h-4 w-4" /> {t("trust.seeT")}
              </p>
              <ul className="mt-4 space-y-3">
                {["trust.see1", "trust.see2", "trust.see3"].map((k) => (
                  <li key={k} className="flex gap-2.5 text-sm leading-relaxed text-slate-300">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" /> {t(k)}
                  </li>
                ))}
              </ul>
            </div>
            <div className="fade-up rounded-3xl border border-rose-500/25 bg-rose-500/[0.04] p-7" style={{ animationDelay: "90ms" }} data-testid="trust-not">
              <p className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-rose-400">
                <EyeOff className="h-4 w-4" /> {t("trust.notT")}
              </p>
              <ul className="mt-4 space-y-3">
                {["trust.not1", "trust.not2", "trust.not3"].map((k) => (
                  <li key={k} className="flex gap-2.5 text-sm leading-relaxed text-slate-300">
                    <X className="mt-0.5 h-4 w-4 shrink-0 text-rose-400" /> {t(k)}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <p className="mt-7 max-w-2xl text-sm leading-relaxed text-slate-400">{t("trust.note")}</p>

          <Button asChild variant="outline" className="mt-7 h-12 border-slate-700 bg-transparent px-6 text-slate-200 hover:bg-slate-800">
            <a href="/garanzie" data-testid="trust-cta-link">
              {t("trust.cta")} <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </Button>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20 lg:px-8 lg:py-28" data-testid="pricing-section">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-500">{t("price.overline")}</p>
        <h2 className="font-head mt-3 text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">{t("price.title")}</h2>
        <p className="mt-3 max-w-xl text-sm text-slate-400">{t("price.sub")}</p>

        <div className="mt-8 inline-flex items-center gap-1 rounded-full border border-slate-800 bg-slate-900 p-1" data-testid="billing-cycle-switch">
          {[["month", "price.monthly"], ["year", "price.yearly"]].map(([c, k]) => (
            <button
              key={c}
              data-testid={`cycle-${c}`}
              onClick={() => setCycle(c)}
              className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${
                cycle === c ? "bg-amber-500 text-slate-950" : "text-slate-400 hover:text-slate-100"
              }`}
            >
              {t(k)}
            </button>
          ))}
          <span className="ml-1 mr-2 whitespace-nowrap text-[10px] font-bold uppercase tracking-wider text-amber-500" data-testid="yearly-badge">
            {t("price.save")}
          </span>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
          {plans.map((pl) => {
            const popular = pl.id === "standard";
            const yearly = cycle === "year";
            return (
              <div
                key={pl.id}
                data-testid={`plan-card-${pl.id}`}
                className={`fade-up relative rounded-3xl border p-8 ${
                  popular
                    ? "border-amber-500/50 bg-slate-900 shadow-[0_0_40px_-15px_rgba(245,158,11,0.35)]"
                    : "border-slate-800 bg-slate-900"
                }`}
              >
                {popular && (
                  <span className="absolute -top-3 left-8 rounded-full bg-amber-500 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-950">
                    {t("price.popular")}
                  </span>
                )}
                <h3 className="font-head text-lg font-bold">{t(`plan.${pl.id}.name`)}</h3>
                <p className="font-num mt-4 text-4xl font-bold">
                  {yearly && pl.price_year ? pl.price_year : pl.price}
                  <span className="text-base font-medium text-slate-500"> €</span>
                </p>
                <p className="text-xs text-slate-500">
                  {yearly && pl.price_year ? t("price.year") : t("price.month")}
                  {pl.price > 0 && " + IVA"}
                </p>
                {yearly && pl.price_year && (
                  <p className="mt-2 text-xs font-semibold text-amber-500" data-testid={`plan-save-${pl.id}`}>
                    {t("price.saveNote", { amount: `${pl.price * 12 - pl.price_year} €` })}
                  </p>
                )}
                <ul className="mt-6 space-y-2">
                  {t(`plan.${pl.id}.h`).split("|").map((h) => (
                    <li key={h} className="flex items-start gap-2 text-sm text-slate-300">
                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" /> {h}
                    </li>
                  ))}
                </ul>
                <Button
                  className={`mt-8 h-12 w-full ${
                    popular
                      ? "bg-amber-500 text-slate-950 hover:bg-amber-400"
                      : "border border-amber-500/50 bg-transparent text-amber-500 hover:bg-amber-500/10 hover:text-amber-400"
                  }`}
                  onClick={() => setQuiz(true)}
                  data-testid={`plan-cta-${pl.id}`}
                >
                  {t("price.cta")}
                </Button>
              </div>
            );
          })}
        </div>
        <p className="mt-6 text-xs text-slate-500" data-testid="price-vat-note">{t("price.vat")}</p>
      </section>

      <section className="border-t border-slate-800">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-8">
          <h2 className="font-head max-w-2xl text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">{t("final.title")}</h2>
          <p className="mt-4 max-w-xl text-sm text-slate-400">{t("final.sub")}</p>
          <Button
            className="mt-8 h-14 bg-amber-500 px-7 text-base text-slate-950 transition-transform hover:scale-[1.02] hover:bg-amber-400"
            onClick={() => setQuiz(true)}
            data-testid="final-quiz-btn"
          >
            <Sparkles className="mr-2 h-4 w-4" /> {t("hero.cta")}
          </Button>
        </div>
      </section>

      <footer className="border-t border-slate-800 bg-slate-900/40">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 px-6 py-10 text-xs text-slate-500 sm:flex-row sm:items-center sm:justify-between lg:px-8">
          <p>
            <span className="font-head font-bold text-slate-300">Reprehendo</span> — {t("footer.tag")}
          </p>
          <p>
            © {new Date().getFullYear()} Reprehendo · {t("footer.rights")}
          </p>
          <div className="flex flex-wrap gap-4">
            <a href="/rh-console" className="transition-colors hover:text-amber-400" data-testid="footer-console-link">Console</a>
            <a href="/biglietto" className="transition-colors hover:text-amber-400" data-testid="footer-kit-link">Materiale</a>
            <a href="/termini" className="transition-colors hover:text-amber-400" data-testid="footer-terms-link">{t("footer.terms")}</a>
            <a href="/garanzie" className="transition-colors hover:text-amber-400" data-testid="footer-garanzie-link">Garanzie</a>
            <a href="/privacy" className="transition-colors hover:text-amber-400" data-testid="footer-privacy-link">{t("footer.privacy")}</a>
            <a href="/sicurezza" className="transition-colors hover:text-amber-400" data-testid="footer-security-link">{t("footer.security")}</a>
            <a href="/stato" className="transition-colors hover:text-amber-400" data-testid="footer-status-link">{t("footer.status")}</a>
          </div>
        </div>
      </footer>

      <Dialog open={quiz} onOpenChange={setQuiz}>        <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto p-0 [&>button]:hidden" data-testid="landing-quiz-dialog">
          <DialogTitle className="sr-only">{t("wiz.public")}</DialogTitle>
          <DialogDescription className="sr-only">{t("final.sub")}</DialogDescription>
          <SetupWizard key={lang} publicMode onClose={() => setQuiz(false)} />
        </DialogContent>
      </Dialog>

      <Questionario open={feedback} onOpenChange={setFeedback} />
    </div>
  );
}
